import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Crown, Check, ArrowRight, Shield, Lock, Star, Settings, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

const PLANS = [
  {
    id: 'weekly',
    name: '1 Semaine',
    price: 20,
    pricePerWeek: 20,
    pricePerMonth: null,
    duration: '7 jours',
    popular: false
  },
  {
    id: 'monthly',
    name: '1 Mois',
    price: 50,
    pricePerWeek: 11.54,
    pricePerMonth: 50,
    duration: '1 mois',
    popular: false,
    savings: '42%'
  },
  {
    id: 'quarterly',
    name: '3 Mois',
    price: 120,
    pricePerWeek: 9.23,
    pricePerMonth: 40,
    duration: '3 mois',
    popular: true,
    savings: '54%'
  },
  {
    id: 'semiannual',
    name: '6 Mois',
    price: 180,
    pricePerWeek: 6.92,
    pricePerMonth: 30,
    duration: '6 mois',
    popular: false,
    savings: '65%'
  },
  {
    id: 'annual',
    name: '1 An',
    price: 240,
    pricePerWeek: 4.62,
    pricePerMonth: 20,
    duration: '12 mois',
    popular: false,
    savings: '77%',
    bestValue: true
  }
];

const FEATURES = [
  'Accès à toutes les photos des profils',
  'Messagerie illimitée',
  'Chat en temps réel',
  'Recherche avancée avec filtres',
  'Photos privées (avec autorisation)',
  'Validation des nouveaux membres',
  'Support prioritaire'
];

export default function SubscriptionPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, api, refreshUser } = useAuth();
  const [selectedPlan, setSelectedPlan] = useState('quarterly');
  const [loading, setLoading] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
  }, [searchParams]);

  const checkPaymentStatus = async (sessionId) => {
    setCheckingPayment(true);
    let attempts = 0;
    const maxAttempts = 5;

    const poll = async () => {
      try {
        const response = await api.get(`/subscriptions/verify/${sessionId}`);
        
        if (response.data.payment_status === 'paid' || response.data.status === 'complete') {
          toast.success('Paiement réussi ! Bienvenue parmi les membres Ambassadeur.');
          await refreshUser();
          navigate('/feed');
          return;
        }

        if (response.data.status === 'expired') {
          toast.error('La session de paiement a expiré.');
          setCheckingPayment(false);
          return;
        }

        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          toast.info('Vérification du paiement en cours...');
          setCheckingPayment(false);
        }
      } catch (error) {
        console.error('Payment check error:', error);
        setCheckingPayment(false);
      }
    };

    poll();
  };

  const handleSubscribe = async () => {
    setLoading(true);
    try {
      const response = await api.post('/subscriptions/create', {
        plan: selectedPlan,
        origin_url: window.location.origin
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de la création du paiement';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    setLoading(true);
    try {
      const response = await api.post('/subscriptions/portal', {
        return_url: window.location.href
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Erreur lors de l\'accès au portail de gestion');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir annuler votre abonnement ? Vous conserverez l\'accès jusqu\'à la fin de la période en cours.')) {
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.post('/subscriptions/cancel');
      toast.success(response.data.message);
      await refreshUser();
    } catch (error) {
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Erreur lors de l\'annulation');
      }
    } finally {
      setLoading(false);
    }
  };

  if (user.is_subscribed) {
    return (
      <div className="min-h-screen bg-dark pt-24 pb-12" data-testid="subscription-page">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <div className="glass-card p-12">
            <Crown className="w-16 h-16 text-gold mx-auto mb-6" />
            <h1 className="font-serif text-3xl gold-gradient-text mb-4">
              Vous êtes Ambassadeur
            </h1>
            <p className="text-gray-400 mb-6">
              Votre abonnement {user.subscription_plan} est actif jusqu'au{' '}
              {new Date(user.subscription_end).toLocaleDateString('fr-FR')}
            </p>
            <p className="text-sm text-gray-500 mb-8">
              Renouvellement automatique activé
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/feed" className="btn-gold inline-flex items-center justify-center gap-2">
                Retour au fil d'actualité
              </Link>
              <Button 
                onClick={handleManageSubscription}
                disabled={loading}
                className="btn-outline-gold inline-flex items-center justify-center gap-2"
                data-testid="manage-subscription-btn"
              >
                <Settings className="w-4 h-4" />
                {loading ? 'Chargement...' : 'Gérer mon abonnement'}
              </Button>
            </div>
            
            <div className="mt-8 pt-6 border-t border-white/10">
              <button
                onClick={handleCancelSubscription}
                disabled={loading}
                className="text-sm text-gray-500 hover:text-red-400 transition-colors inline-flex items-center gap-2"
                data-testid="cancel-subscription-btn"
              >
                <XCircle className="w-4 h-4" />
                Annuler mon abonnement
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (checkingPayment) {
    return (
      <div className="min-h-screen bg-dark pt-24 pb-12 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-gold/30 border-t-gold rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Vérification du paiement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark pt-24 pb-12" data-testid="subscription-page">
      <div className="max-w-6xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-12">
          <Crown className="w-16 h-16 text-gold mx-auto mb-6" />
          <h1 className="font-serif text-4xl md:text-5xl gold-gradient-text mb-4">
            Abonnez-vous
          </h1>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Accédez à toutes les fonctionnalités de DiscreetLux : photos, messagerie, chat en direct, recherche avancée...
          </p>
        </div>

        {/* Features */}
        <div className="glass-card p-8 mb-12">
          <h2 className="font-serif text-xl text-gold mb-6">Vos avantages</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {FEATURES.map((feature, index) => (
              <div key={index} className="flex items-center gap-3">
                <Check className="w-5 h-5 text-gold flex-shrink-0" />
                <span className="text-gray-300">{feature}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Plans */}
        <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-4 mb-12" data-testid="plans-grid">
          {PLANS.map((plan) => (
            <button
              key={plan.id}
              onClick={() => setSelectedPlan(plan.id)}
              className={`relative glass-card p-6 text-left transition-all duration-300 ${
                selectedPlan === plan.id 
                  ? 'border-gold ring-2 ring-gold/20' 
                  : 'hover:border-gold/50'
              }`}
              data-testid={`plan-${plan.id}`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-gold text-dark text-xs font-bold">
                  POPULAIRE
                </div>
              )}
              {plan.savings && (
                <div className="absolute -top-3 right-2 px-2 py-1 bg-green-500/20 text-green-400 text-xs font-semibold">
                  -{plan.savings}
                </div>
              )}
              
              <h3 className="font-serif text-lg text-white mb-2">{plan.name}</h3>
              <p className="text-gray-500 text-sm mb-4">{plan.duration}</p>
              
              <div className="mb-2">
                <span className="text-3xl font-bold gold-gradient-text">{plan.pricePerWeek.toFixed(2).replace('.', ',')}€</span>
                <span className="text-gray-500 text-sm">/sem</span>
              </div>
              {plan.pricePerMonth && (
                <p className="text-gray-500 text-xs mb-4">{plan.pricePerMonth}€/mois</p>
              )}
              {!plan.pricePerMonth && (
                <p className="text-gray-500 text-xs mb-4">&nbsp;</p>
              )}

              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                selectedPlan === plan.id 
                  ? 'border-gold bg-gold' 
                  : 'border-white/30'
              }`}>
                {selectedPlan === plan.id && (
                  <Check className="w-3 h-3 text-dark" />
                )}
              </div>
            </button>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center">
          {/* NextDev info - above button */}
          <p className="mb-4 text-center text-gray-400 text-sm">
            Le nom qui apparaîtra sur votre relevé bancaire sera : <strong className="text-gold">NextDev</strong>
          </p>
          
          <Button
            onClick={handleSubscribe}
            disabled={loading}
            className="btn-gold text-lg px-12 py-4 flex items-center gap-3 mx-auto"
            data-testid="subscribe-btn"
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-dark/30 border-t-dark rounded-full animate-spin" />
            ) : (
              <>
                <Crown className="w-5 h-5" />
                S'abonner maintenant
                <ArrowRight className="w-5 h-5" />
              </>
            )}
          </Button>
          
          <div className="mt-6 flex items-center justify-center gap-6 text-sm text-gray-500">
            <span className="flex items-center gap-2">
              <Shield className="w-4 h-4" /> Paiement sécurisé
            </span>
            <span className="flex items-center gap-2">
              <Lock className="w-4 h-4" /> Données protégées
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
